import { FC, useState, SyntheticEvent } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShortTextIcon from '@mui/icons-material/ShortText';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import PictureInPictureOutlinedIcon from '@mui/icons-material/PictureInPictureOutlined';
import { Box, styled, Typography } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { PageMarginsTuner } from './tuners/pageMarginsTuner';
import { ReportObjectWithFormattedTextTuner } from './tuners/reportObjectTuner';
import { ReportImageTuner } from './tuners/reportImageTuner';
import { useTranslation } from 'react-i18next';
import { Theme } from '../../../../../../../shared/styles/theme/theme';
import { IReportConfigurationValuesPaths } from '../reportsConfiguration.tab';
import { ResetButton } from './resetButton/resetButton.component';

export const PaymentSlipConfig: FC<IReportConfigurationValuesPaths> = ({
  valuesPath,
  defaultValuesPath,
}) => {
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);

  const { t } = useTranslation();

  const handleAccordionChange = (panel: string) => (_: SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  return (
    <>
      <SAccordion
        expanded={expandedAccordion === 'pageMargins'}
        onChange={handleAccordionChange('pageMargins')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <AccordionHeader
            icon={ShortTextIcon}
            text={t('settings.page_margins')}
            color={Theme.palette.warning.light}
          />
          {expandedAccordion === 'pageMargins' && (
            <ResetButton
              reportActualValuesPath={valuesPath}
              reportDefaultValuesPath={defaultValuesPath}
              reportObjectName={'pageMargins'}
            />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <PageMarginsTuner
            reportActualValuesPath={valuesPath}
            reportDefaultValuesPath={defaultValuesPath}
            reportObjectName={'pageMargins'}
          />
        </AccordionDetails>
      </SAccordion>

      <SAccordion
        expanded={expandedAccordion === 'customText'}
        onChange={handleAccordionChange('customText')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <AccordionHeader
            icon={EditNoteOutlinedIcon}
            text={t('settings.custom_text')}
            color={Theme.palette.primary.main}
          />
          {expandedAccordion === 'customText' && (
            <ResetButton
              reportActualValuesPath={valuesPath}
              reportDefaultValuesPath={defaultValuesPath}
              reportObjectName={'customText'}
              ignoredFields={['formattedText']}
            />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <ReportObjectWithFormattedTextTuner
            reportActualValuesPath={valuesPath}
            reportDefaultValuesPath={defaultValuesPath}
            reportObjectName={'customText'}
          />
        </AccordionDetails>
      </SAccordion>

      <SAccordion
        expanded={expandedAccordion === 'picture'}
        onChange={handleAccordionChange('picture')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <AccordionHeader
            icon={PictureInPictureOutlinedIcon}
            text={t('settings.picture')}
            color={Theme.palette.success.light}
          />
          {expandedAccordion === 'picture' && (
            <ResetButton
              reportActualValuesPath={valuesPath}
              reportDefaultValuesPath={defaultValuesPath}
              reportObjectName={'picture'}
              ignoredFields={['image']}
            />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <ReportImageTuner
            reportActualValuesPath={valuesPath}
            reportDefaultValuesPath={defaultValuesPath}
            reportObjectName={'picture'}
            maxImgHeight={1500}
            maxImgWidth={1500}
          />
        </AccordionDetails>
      </SAccordion>
    </>
  );
};

const SAccordion = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  '.MuiAccordion-root:first-of-type': {
    borderTopLeftRadius: '4px !important',
    borderTopRightRadius: '4px !important',
  },

  '&.Mui-expanded': {
    backgroundColor: theme.palette.lightGrey.light,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

export const AccordionHeader: FC<IAccordionHeader> = ({ icon, text, color }) => {
  const Icon = styled(icon)(({ theme }) => ({
    width: 24,
    height: 24,
    display: 'inline-block',
    marginRight: theme.spacing(1),
    fill: color,
  }));
  return (
    <Box component='span' sx={{ display: 'flex', alignItems: 'center' }}>
      <Icon />
      <Typography component='span' variant='body1' color={color}>
        {text}
      </Typography>
    </Box>
  );
};

interface IAccordionHeader {
  icon: any;
  text: string;
  color: string;
}
